import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, GripVertical, Loader2, Save, Video, Clock, Dumbbell, Heart, RotateCcw, Search, Filter, X, Layers, Copy } from 'lucide-react';
import OfficialExercisePicker from '../../components/OfficialExercisePicker';
import ExerciseSearchSelect from '../../components/admin/ExerciseSearchSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Muscle group configuration with colors
const MUSCLE_GROUPS = [
  { key: 'all', label: 'Todos', color: 'bg-primary' },
  { key: 'pectoral', label: 'Pectoral', color: 'bg-red-500', keywords: ['pectoral', 'pecho', 'press'] },
  { key: 'espalda', label: 'Espalda', color: 'bg-blue-500', keywords: ['dorsal', 'espalda', 'trapecio', 'romboides', 'lumbar', 'erectores', 'remo', 'jalón', 'pulldown'] },
  { key: 'hombro', label: 'Hombro', color: 'bg-orange-500', keywords: ['deltoides', 'hombro', 'elevaciones'] },
  { key: 'biceps', label: 'Bíceps', color: 'bg-green-500', keywords: ['bíceps', 'biceps', 'curl'] },
  { key: 'triceps', label: 'Tríceps', color: 'bg-purple-500', keywords: ['tríceps', 'triceps', 'extensión', 'extension', 'fondos'] },
  { key: 'pierna', label: 'Pierna', color: 'bg-yellow-500', keywords: ['cuádriceps', 'cuadriceps', 'isquiotibiales', 'femoral', 'glúteo', 'gluteo', 'aductor', 'abductor', 'pierna', 'sentadilla', 'squat', 'prensa', 'zancada', 'extensión de pierna'] },
  { key: 'pantorrilla', label: 'Pantorrilla', color: 'bg-pink-500', keywords: ['gemelo', 'sóleo', 'soleo', 'pantorrilla'] },
  { key: 'core', label: 'Core/ABD', color: 'bg-cyan-500', keywords: ['abdomen', 'abdominal', 'oblicuo', 'core', 'recto', 'plank', 'crunch'] },
  { key: 'antebrazo', label: 'Antebrazo', color: 'bg-amber-500', keywords: ['antebrazo', 'muñeca', 'agarre'] },
];

// Block types for ordering
const BLOCK_TYPES = [
  { value: 'warmup', label: '🔥 Calentamiento', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'cardio', label: '❤️ Resistencia/Cardio', color: 'bg-red-500/20 text-red-400' },
  { value: 'strength', label: '💪 Fuerza', color: 'bg-amber-500/20 text-amber-400' },
  { value: 'circuit', label: '🔄 Circuito', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'core', label: '🎯 Core/ABD', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'stretching', label: '🧘 Estiramientos', color: 'bg-green-500/20 text-green-400' },
];

// Tempo options for exercise control
const TEMPO_OPTIONS = [
  { value: '1:1', label: '1:1', description: 'Normal' },
  { value: '1:2', label: '1:2', description: 'Excéntrico lento' },
  { value: '1:3', label: '1:3', description: 'Excéntrico muy lento' },
  { value: '2:1', label: '2:1', description: 'Concéntrico lento' },
  { value: '2:2', label: '2:2', description: 'Controlado' },
  { value: '2:3', label: '2:3', description: 'Control máximo' },
  { value: '3:1', label: '3:1', description: 'Fuerza explosiva' },
  { value: '3:3', label: '3:3', description: 'Tiempo bajo tensión' },
];

// Cardio machines
const CARDIO_MACHINES = [
  { value: 'cinta', label: 'Cinta de correr', icon: '🏃' },
  { value: 'eliptica', label: 'Elíptica', icon: '🔄' },
  { value: 'remo', label: 'Remo', icon: '🚣' },
  { value: 'bike', label: 'Bicicleta', icon: '🚴' },
  { value: 'assault', label: 'Assault Bike', icon: '💨' },
  { value: 'skyline', label: 'Escaladora/Skyline', icon: '🪜' },
];

// Intensity levels
const INTENSITY_LEVELS = [
  { value: 'baja', label: 'Baja', color: 'text-green-400' },
  { value: 'media', label: 'Media', color: 'text-yellow-400' },
  { value: 'alta', label: 'Alta', color: 'text-orange-400' },
  { value: 'intervalos', label: 'Intervalos (HIIT)', color: 'text-red-400' },
];

const PlanBuilderPage = ({ LayoutComponent = AdminLayout, basePath = '/admin/plans' }) => {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'new';
  
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [expandingMeso, setExpandingMeso] = useState(false);
  const [exercises, setExercises] = useState([]);
  
  // Exercise picker dialog state
  const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
  const [exercisePickerContext, setExercisePickerContext] = useState(null); // {weekIndex, sessionIndex, type: 'strength'|'circuit', circuitIndex}
  const [insertPosition, setInsertPosition] = useState(null); // {weekIndex, sessionIndex, position}
  const [showExerciseSearch, setShowExerciseSearch] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [exerciseFilter, setExerciseFilter] = useState('all');
  
  const [planData, setPlanData] = useState({
    name: '',
    objective: '',
    description: '',
    weeks: []
  });

  // Get muscle group for an exercise
  const getMuscleGroup = (exercise) => {
    const name = (exercise.name || '').toLowerCase();
    const desc = (exercise.description || '').toLowerCase();
    const combined = `${name} ${desc}`;
    for (const group of MUSCLE_GROUPS) {
      if (group.key === 'all') continue;
      if (group.keywords?.some(keyword => combined.includes(keyword))) {
        return group;
      }
    }
    return { key: 'other', label: 'Otros', color: 'bg-gray-500' };
  };

  // Filtered exercises for picker
  const filteredExercises = useMemo(() => {
    return exercises.filter(exercise => {
      const matchesSearch = exerciseSearch === '' || 
        exercise.name.toLowerCase().includes(exerciseSearch.toLowerCase()) ||
        (exercise.description || '').toLowerCase().includes(exerciseSearch.toLowerCase());
      
      if (exerciseFilter === 'all') {
        return matchesSearch;
      }
      
      const group = getMuscleGroup(exercise);
      return matchesSearch && group.key === exerciseFilter;
    });
  }, [exercises, exerciseSearch, exerciseFilter]);

  // Open exercise picker
  const openExercisePicker = (weekIndex, sessionIndex, type, circuitIndex = null) => {
    setExercisePickerContext({ weekIndex, sessionIndex, type, circuitIndex });
    setExerciseSearch('');
    setExerciseFilter('all');
    setExercisePickerOpen(true);
  };

  // Handle exercise selection from picker
  const handleExerciseSelect = (exerciseId) => {
    // Si es inserción en posición específica
    if (insertPosition) {
      insertExerciseAtPosition(
        insertPosition.weekIndex, 
        insertPosition.sessionIndex, 
        insertPosition.position, 
        exerciseId
      );
      setInsertPosition(null);
      setShowExerciseSearch(false);
      return;
    }
    
    if (!exercisePickerContext) return;
    
    const { weekIndex, sessionIndex, type, circuitIndex } = exercisePickerContext;
    
    if (type === 'strength') {
      addExerciseToSession(weekIndex, sessionIndex, exerciseId);
    } else if (type === 'circuit' && circuitIndex !== null) {
      addExerciseToCircuit(weekIndex, sessionIndex, circuitIndex, exerciseId);
    }
    
    setExercisePickerOpen(false);
  };

  useEffect(() => {
    fetchExercises();
    if (isEditing) {
      fetchPlan();
    }
  }, [id]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/plans/${id}`);
      setPlanData(response.data);
    } catch (error) {
      console.error('Failed to fetch plan:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercises`);
      setExercises(response.data);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/plans/${id}`, planData);
        toast.success(t('planUpdated'));
      } else {
        await axios.post(`${API}/plans`, planData);
        toast.success(t('planCreated'));
      }
      navigate(basePath);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const addWeek = () => {
    setPlanData(prev => ({
      ...prev,
      weeks: [...prev.weeks, {
        week_number: prev.weeks.length + 1,
        name: `${t('week')} ${prev.weeks.length + 1}`,
        sessions: []
      }]
    }));
  };

  // Expandir plan a mesociclo completo (replicar semana 1 → 5 semanas)
  const expandToMesocycle = async () => {
    if (!isEditing) {
      toast.error('Primero guarda el plan antes de expandir');
      return;
    }
    
    if (planData.weeks.length >= 5) {
      toast.info(`El plan ya tiene ${planData.weeks.length} semanas`);
      return;
    }
    
    if (planData.weeks.length === 0) {
      toast.error('El plan necesita al menos 1 semana base');
      return;
    }
    
    const confirm = window.confirm(
      `¿Expandir a Mesociclo completo?\n\n` +
      `La Semana 1 se replicará para crear las Semanas 2-5.\n` +
      `Total: 5 semanas idénticas = 1 Mesociclo`
    );
    
    if (!confirm) return;
    
    try {
      setExpandingMeso(true);
      const response = await axios.post(`${API}/admin/plans/${id}/expand-to-mesocycle`);
      toast.success(response.data.message);
      fetchPlan(); // Recargar el plan
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al expandir');
    } finally {
      setExpandingMeso(false);
    }
  };

  // Crear nuevo mesociclo (basado en la última semana del mesociclo anterior)
  const createNewMesocycle = async () => {
    if (!isEditing) {
      toast.error('Primero guarda el plan');
      return;
    }
    
    if (planData.weeks.length === 0) {
      toast.error('El plan necesita al menos 1 semana');
      return;
    }
    
    if (planData.weeks.length % 5 !== 0) {
      toast.error(`Completa primero el mesociclo actual (${planData.weeks.length}/5 semanas)`);
      return;
    }
    
    const currentMeso = planData.weeks.length / 5;
    const nextMeso = currentMeso + 1;
    
    if (nextMeso > 5) {
      toast.info('El macrociclo ya está completo (5 mesociclos = 25 semanas)');
      return;
    }
    
    const confirm = window.confirm(
      `¿Crear Mesociclo ${nextMeso}?\n\n` +
      `Se replicará la última semana del plan (Semana ${planData.weeks.length}) como base.\n` +
      `Se crearán las Semanas ${planData.weeks.length + 1}-${planData.weeks.length + 5}`
    );
    
    if (!confirm) return;
    
    try {
      setExpandingMeso(true);
      // Usar la última semana como base para el nuevo mesociclo
      const lastWeek = planData.weeks[planData.weeks.length - 1];
      
      const response = await axios.post(`${API}/admin/plans/${id}/create-new-mesocycle`, {
        sessions: lastWeek.sessions
      });
      
      toast.success(response.data.message);
      fetchPlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear mesociclo');
    } finally {
      setExpandingMeso(false);
    }
  };

  // Calcular info del mesociclo actual
  const mesocycleInfo = useMemo(() => {
    const totalWeeks = planData.weeks.length;
    const currentMeso = Math.ceil(totalWeeks / 5) || 1;
    const weeksInCurrentMeso = totalWeeks % 5 || (totalWeeks > 0 ? 5 : 0);
    const isComplete = totalWeeks > 0 && totalWeeks % 5 === 0;
    
    // canCompleteMeso: cuando hay 1 semana en un nuevo mesociclo (ej: semana 6, 11, 16...)
    // y queremos duplicarla para completar las 5 semanas del mesociclo
    const hasFirstWeekOfNewMeso = totalWeeks > 5 && totalWeeks % 5 === 1;
    
    return {
      current: currentMeso,
      weeksInCurrent: weeksInCurrentMeso,
      totalWeeks,
      isComplete,
      canExpand: totalWeeks > 0 && totalWeeks < 5,
      canCreateNew: isComplete && currentMeso < 5,
      canCompleteMeso: hasFirstWeekOfNewMeso && currentMeso <= 5  // Nueva opción
    };
  }, [planData.weeks.length]);

  const removeWeek = (weekIndex) => {
    setPlanData(prev => ({
      ...prev,
      weeks: prev.weeks.filter((_, i) => i !== weekIndex).map((w, i) => ({
        ...w,
        week_number: i + 1
      }))
    }));
  };

  const addSession = (weekIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions.push({
        name: `${t('session')} ${newWeeks[weekIndex].sessions.length + 1}`,
        description: '',
        exercises: [],
        cardio_blocks: [],
        circuit_blocks: [],
        block_order: ['warmup', 'strength', 'core', 'stretching']  // Default order
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  const removeSession = (weekIndex, sessionIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions = newWeeks[weekIndex].sessions.filter((_, i) => i !== sessionIndex);
      return { ...prev, weeks: newWeeks };
    });
  };

  const updateSession = (weekIndex, sessionIndex, field, value) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex][field] = value;
      return { ...prev, weeks: newWeeks };
    });
  };

  // ============ STRENGTH EXERCISES ============
  const addExerciseToSession = (weekIndex, sessionIndex, exerciseId) => {
    const exercise = exercises.find(e => e.id === exerciseId);
    if (!exercise) return;

    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      const session = newWeeks[weekIndex].sessions[sessionIndex];
      if (!session.exercises) session.exercises = [];
      session.exercises.push({
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        sets: 3,
        reps: '10',
        time: '',
        rest: '60s',
        tempo: '1:2',
        notes: '',
        video_url: exercise.video_url || '',
        order: session.exercises.length,
        // Soporte para biseries
        is_biseries: false,
        biseries_exercise_id: '',
        biseries_exercise_name: '',
        // Soporte para triseries (biserie + 3er ejercicio)
        is_triseries: false,
        triseries_exercise_id: '',
        triseries_exercise_name: ''
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  // Insertar ejercicio en una posición específica
  const insertExerciseAtPosition = (weekIndex, sessionIndex, position, exerciseId) => {
    const exercise = exercises.find(e => e.id === exerciseId);
    if (!exercise) return;

    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      const session = newWeeks[weekIndex].sessions[sessionIndex];
      if (!session.exercises) session.exercises = [];
      
      const newExercise = {
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        sets: 3,
        reps: '10',
        time: '',
        rest: '60s',
        tempo: '1:2',
        notes: '',
        video_url: exercise.video_url || '',
        order: position,
        is_biseries: false,
        biseries_exercise_id: '',
        biseries_exercise_name: '',
        is_triseries: false,
        triseries_exercise_id: '',
        triseries_exercise_name: ''
      };
      
      // Insertar en la posición y reordenar
      session.exercises.splice(position, 0, newExercise);
      // Actualizar orden de todos
      session.exercises.forEach((ex, idx) => ex.order = idx);
      
      return { ...prev, weeks: newWeeks };
    });
  };

  const updateExercise = (weekIndex, sessionIndex, exerciseIndex, field, value) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].exercises[exerciseIndex][field] = value;
      return { ...prev, weeks: newWeeks };
    });
  };

  // Vincula un ejercicio del plan a un ejercicio oficial de la librería v2
  // en una sola actualización atómica (evita problemas de batching de setState)
  const linkExerciseToOfficial = (weekIndex, sessionIndex, exerciseIndex, picked) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      const ex = newWeeks[weekIndex].sessions[sessionIndex].exercises[exerciseIndex];
      ex.exercise_id = picked.exercise_id;
      ex.exercise_name = picked.nombre_oficial;
      if (picked.video_url) ex.video_url = picked.video_url;
      if (picked.video_filename) ex.video_filename = picked.video_filename;
      return { ...prev, weeks: newWeeks };
    });
  };

  const removeExercise = (weekIndex, sessionIndex, exerciseIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].exercises = 
        newWeeks[weekIndex].sessions[sessionIndex].exercises.filter((_, i) => i !== exerciseIndex);
      return { ...prev, weeks: newWeeks };
    });
  };

  // ============ CARDIO BLOCKS ============
  const addCardioBlock = (weekIndex, sessionIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      const session = newWeeks[weekIndex].sessions[sessionIndex];
      if (!session.cardio_blocks) session.cardio_blocks = [];
      session.cardio_blocks.push({
        type: 'cardio',
        machine: 'cinta',
        total_time: '20min',
        sets: 1,
        set_time: '',
        rest_between_sets: '',
        intensity: 'media',
        notes: '',
        order: session.cardio_blocks.length
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  const updateCardioBlock = (weekIndex, sessionIndex, cardioIndex, field, value) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].cardio_blocks[cardioIndex][field] = value;
      return { ...prev, weeks: newWeeks };
    });
  };

  const removeCardioBlock = (weekIndex, sessionIndex, cardioIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].cardio_blocks = 
        newWeeks[weekIndex].sessions[sessionIndex].cardio_blocks.filter((_, i) => i !== cardioIndex);
      return { ...prev, weeks: newWeeks };
    });
  };

  // ============ CIRCUIT BLOCKS ============
  const addCircuitBlock = (weekIndex, sessionIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      const session = newWeeks[weekIndex].sessions[sessionIndex];
      if (!session.circuit_blocks) session.circuit_blocks = [];
      session.circuit_blocks.push({
        type: 'circuit',
        name: `Circuito ${session.circuit_blocks.length + 1}`,
        rounds: 3,
        exercises: [],
        exercise_time: '40s',
        rest_between_exercises: '20s',
        rest_between_rounds: '90s',
        notes: '',
        order: session.circuit_blocks.length
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  const updateCircuitBlock = (weekIndex, sessionIndex, circuitIndex, field, value) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks[circuitIndex][field] = value;
      return { ...prev, weeks: newWeeks };
    });
  };

  const removeCircuitBlock = (weekIndex, sessionIndex, circuitIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks = 
        newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks.filter((_, i) => i !== circuitIndex);
      return { ...prev, weeks: newWeeks };
    });
  };

  const addExerciseToCircuit = (weekIndex, sessionIndex, circuitIndex, exerciseId) => {
    const exercise = exercises.find(e => e.id === exerciseId);
    if (!exercise) return;

    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      const circuit = newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks[circuitIndex];
      circuit.exercises.push({
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        time: '40s',
        reps: '',
        video_url: exercise.video_url || ''
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  const updateCircuitExercise = (weekIndex, sessionIndex, circuitIndex, exerciseIndex, field, value) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks[circuitIndex].exercises[exerciseIndex][field] = value;
      return { ...prev, weeks: newWeeks };
    });
  };

  const removeCircuitExercise = (weekIndex, sessionIndex, circuitIndex, exerciseIndex) => {
    setPlanData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks[circuitIndex].exercises = 
        newWeeks[weekIndex].sessions[sessionIndex].circuit_blocks[circuitIndex].exercises.filter((_, i) => i !== exerciseIndex);
      return { ...prev, weeks: newWeeks };
    });
  };

  if (loading) {
    return (
      <LayoutComponent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </LayoutComponent>
    );
  }

  return (
    <LayoutComponent>
      <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in" data-testid="plan-builder">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => navigate(basePath)}
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('back')}
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditing ? t('editPlan') : t('newPlan')}
            </h1>
          </div>
          <Button 
            type="submit" 
            className="bg-primary text-primary-foreground"
            disabled={submitting}
            data-testid="save-plan-button"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t('save')}
          </Button>
        </div>

        {/* Basic Info */}
        <Card className="bg-card border-border">
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('planName')}</Label>
                <Input
                  value={planData.name}
                  onChange={(e) => setPlanData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="plan-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('objective')}</Label>
                <Input
                  value={planData.objective}
                  onChange={(e) => setPlanData(prev => ({ ...prev, objective: e.target.value }))}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid="plan-objective-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('description')}</Label>
              <Textarea
                value={planData.description}
                onChange={(e) => setPlanData(prev => ({ ...prev, description: e.target.value }))}
                className="bg-zinc-900/50 border-zinc-800"
                data-testid="plan-description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Weeks */}
        <div className="space-y-4">
          {/* Header con info de mesociclo */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('weeks')}</h2>
              {isEditing && mesocycleInfo.totalWeeks > 0 && (
                <p className="text-sm text-muted-foreground">
                  Mesociclo {mesocycleInfo.current} • {mesocycleInfo.weeksInCurrent}/5 semanas
                  {mesocycleInfo.isComplete && ' ✅'}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Botón Expandir a Mesociclo - Solo si hay 1-4 semanas */}
              {isEditing && mesocycleInfo.canExpand && (
                <Button 
                  type="button" 
                  variant="outline"
                  className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={expandToMesocycle}
                  disabled={expandingMeso}
                  data-testid="expand-mesocycle-button"
                >
                  {expandingMeso ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Completar Mesociclo (→5 sem)
                </Button>
              )}
              
              {/* Botón Crear Nuevo Mesociclo - Solo si mesociclo completo y no ha llegado a 5 */}
              {isEditing && mesocycleInfo.canCreateNew && (
                <Button 
                  type="button" 
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={createNewMesocycle}
                  disabled={expandingMeso}
                  data-testid="create-mesocycle-button"
                >
                  {expandingMeso ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Layers className="h-4 w-4 mr-2" />
                  )}
                  + Mesociclo {mesocycleInfo.current + 1}
                </Button>
              )}
              
              {/* Botón Completar Mesociclo Actual - Siempre visible si hay semanas */}
              {isEditing && planData.weeks.length > 0 && (
                <Button 
                  type="button" 
                  variant="outline"
                  className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                  onClick={async () => {
                    if (!window.confirm(`¿Completar Mesociclo?\n\nEsto tomará la semana con contenido y la duplicará hasta completar 5 semanas. Las semanas vacías se eliminarán.`)) return;
                    
                    setExpandingMeso(true);
                    try {
                      const response = await axios.post(`${API}/admin/plans/${id}/fill-mesocycle`);
                      toast.success(response.data.message || 'Mesociclo completado');
                      const planRes = await axios.get(`${API}/plans/${id}`);
                      setPlanData(planRes.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Error');
                    } finally {
                      setExpandingMeso(false);
                    }
                  }}
                  disabled={expandingMeso}
                  data-testid="complete-mesocycle-button"
                >
                  {expandingMeso ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Layers className="h-4 w-4 mr-2" />
                  )}
                  Completar Meso
                </Button>
              )}
              
              <Button 
                type="button" 
                variant="outline" 
                onClick={addWeek}
                data-testid="add-week-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('addWeek')}
              </Button>
              
              {/* Botón Duplicar Última Semana - Siempre visible si hay al menos 1 semana */}
              {isEditing && planData.weeks.length > 0 && (
                <Button 
                  type="button" 
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  onClick={async () => {
                    const lastWeek = planData.weeks.length;
                    if (!window.confirm(`¿Duplicar semana ${lastWeek}?\n\nSe creará la semana ${lastWeek + 1} con todo el contenido de la semana ${lastWeek}.`)) return;
                    
                    setExpandingMeso(true);
                    try {
                      const response = await axios.post(`${API}/admin/plans/${id}/duplicate-last-week`);
                      toast.success(response.data.message || 'Semana duplicada');
                      const planRes = await axios.get(`${API}/plans/${id}`);
                      setPlanData(planRes.data);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Error');
                    } finally {
                      setExpandingMeso(false);
                    }
                  }}
                  disabled={expandingMeso}
                  data-testid="duplicate-week-button"
                >
                  {expandingMeso ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  Duplicar Semana
                </Button>
              )}
            </div>
          </div>

          {planData.weeks.length === 0 ? (
            <Card className="bg-card border-border border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground mb-4">
                  {t('noPlans')}
                </p>
                <Button type="button" variant="outline" onClick={addWeek}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addWeek')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="multiple" className="space-y-4">
              {planData.weeks.map((week, weekIndex) => (
                <AccordionItem 
                  key={weekIndex} 
                  value={`week-${weekIndex}`}
                  className="bg-card border border-border rounded-sm"
                >
                  <AccordionTrigger className="px-6 hover:no-underline" data-testid={`week-${weekIndex}-trigger`}>
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{t('week')} {week.week_number}</span>
                      <span className="text-sm text-muted-foreground">
                        ({week.sessions.length} {t('sessions').toLowerCase()})
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6">
                    <div className="space-y-4">
                      {/* Week Name */}
                      <div className="flex items-center gap-4">
                        <Input
                          value={week.name}
                          onChange={(e) => {
                            const newWeeks = [...planData.weeks];
                            newWeeks[weekIndex].name = e.target.value;
                            setPlanData(prev => ({ ...prev, weeks: newWeeks }));
                          }}
                          className="bg-zinc-900/50 border-zinc-800 flex-1"
                          placeholder={t('week')}
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeWeek(weekIndex)}
                          className="text-destructive"
                          data-testid={`remove-week-${weekIndex}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Sessions */}
                      <div className="space-y-3 ml-4">
                        {week.sessions.map((session, sessionIndex) => (
                          <Card key={sessionIndex} className="bg-surface border-border">
                            <CardContent className="p-4 space-y-4">
                              <div className="flex items-center gap-4">
                                <Input
                                  value={session.name}
                                  onChange={(e) => updateSession(weekIndex, sessionIndex, 'name', e.target.value)}
                                  className="bg-zinc-900/50 border-zinc-800 flex-1"
                                  placeholder={t('session')}
                                  data-testid={`session-${weekIndex}-${sessionIndex}-name`}
                                />
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => removeSession(weekIndex, sessionIndex)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>

                              {/* BLOCK ORDER SELECTOR */}
                              <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground">Orden de bloques (arrastra para reordenar)</Label>
                                <div className="flex flex-wrap gap-2">
                                  {(session.block_order || ['warmup', 'strength', 'core', 'stretching']).map((blockType, blockIndex) => {
                                    const block = BLOCK_TYPES.find(b => b.value === blockType);
                                    return block ? (
                                      <div 
                                        key={blockType}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${block.color} cursor-move`}
                                      >
                                        <span className="text-muted-foreground">{blockIndex + 1}.</span>
                                        {block.label}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOrder = [...(session.block_order || ['warmup', 'strength', 'core', 'stretching'])];
                                            newOrder.splice(blockIndex, 1);
                                            updateSession(weekIndex, sessionIndex, 'block_order', newOrder);
                                          }}
                                          className="ml-1 text-muted-foreground hover:text-destructive"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ) : null;
                                  })}
                                  <Select 
                                    onValueChange={(value) => {
                                      const currentOrder = session.block_order || ['warmup', 'strength', 'core', 'stretching'];
                                      if (!currentOrder.includes(value)) {
                                        updateSession(weekIndex, sessionIndex, 'block_order', [...currentOrder, value]);
                                      }
                                    }}
                                  >
                                    <SelectTrigger className="w-32 h-8 bg-zinc-900/50 border-zinc-800 border-dashed text-xs">
                                      <SelectValue placeholder="+ Añadir" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {BLOCK_TYPES.filter(b => !(session.block_order || ['warmup', 'strength', 'core', 'stretching']).includes(b.value)).map((block) => (
                                        <SelectItem key={block.value} value={block.value}>
                                          {block.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              {/* TABS: Fuerza, Resistencia, Circuitos */}
                              <Tabs defaultValue="fuerza" className="w-full">
                                <TabsList className="grid w-full grid-cols-3 bg-zinc-900/50">
                                  <TabsTrigger value="fuerza" className="flex items-center gap-2 data-[state=active]:bg-primary/20">
                                    <Dumbbell className="h-4 w-4" />
                                    Fuerza
                                  </TabsTrigger>
                                  <TabsTrigger value="resistencia" className="flex items-center gap-2 data-[state=active]:bg-red-500/20">
                                    <Heart className="h-4 w-4" />
                                    Resistencia
                                  </TabsTrigger>
                                  <TabsTrigger value="circuitos" className="flex items-center gap-2 data-[state=active]:bg-green-500/20">
                                    <RotateCcw className="h-4 w-4" />
                                    Circuitos
                                  </TabsTrigger>
                                </TabsList>

                                {/* TAB: FUERZA */}
                                <TabsContent value="fuerza" className="space-y-2 mt-4">
                                  {/* Botón insertar al inicio */}
                                  {session.exercises?.length > 0 && (
                                    <div className="flex justify-center">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setInsertPosition({ weekIndex, sessionIndex, position: 0 });
                                          setShowExerciseSearch(true);
                                        }}
                                        className="px-3 py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-sm transition-colors flex items-center gap-1"
                                        title="Insertar ejercicio aquí"
                                      >
                                        <Plus className="h-3 w-3" /> Insertar aquí
                                      </button>
                                    </div>
                                  )}
                                  {session.exercises?.map((exercise, exerciseIndex) => (
                                    <React.Fragment key={exerciseIndex}>
                                    <div 
                                      className={`p-3 rounded-sm ${
                                        exercise.is_triseries
                                          ? 'bg-amber-500/10 border border-amber-500/30'
                                          : exercise.is_biseries
                                            ? 'bg-purple-500/10 border border-purple-500/30'
                                            : 'bg-black/20'
                                      }`}
                                    >
                                      {/* Header con nombre y toggle biserie/triserie */}
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2 flex-1">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <p className="text-sm font-medium text-foreground">
                                                {exercise.is_triseries && <span className="text-amber-400 mr-1">⚡⚡ TRISERIE:</span>}
                                                {!exercise.is_triseries && exercise.is_biseries && <span className="text-purple-400 mr-1">⚡ BISERIE:</span>}
                                                {exercise.exercise_name}
                                              </p>
                                              <OfficialExercisePicker
                                                currentName={exercise.exercise_name}
                                                currentExerciseId={exercise.exercise_id}
                                                compact
                                                onPick={(picked) => {
                                                  linkExerciseToOfficial(weekIndex, sessionIndex, exerciseIndex, picked);
                                                  toast.success(`Vinculado a ${picked.exercise_id}`);
                                                }}
                                              />
                                            </div>
                                            {(exercise.is_biseries || exercise.is_triseries) && exercise.biseries_exercise_name && (
                                              <p className="text-sm text-purple-300">
                                                + {exercise.biseries_exercise_name}
                                              </p>
                                            )}
                                            {exercise.is_triseries && exercise.triseries_exercise_name && (
                                              <p className="text-sm text-amber-300">
                                                + {exercise.triseries_exercise_name}
                                              </p>
                                            )}
                                            {exercise.video_url && (
                                              <a href={exercise.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1">
                                                <Video className="h-3 w-3" /> Video
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {/* Toggle Biserie */}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              // Al activar biserie, desactivar triserie
                                              const newVal = !exercise.is_biseries;
                                              updateExercise(weekIndex, sessionIndex, exerciseIndex, 'is_biseries', newVal);
                                              if (newVal && exercise.is_triseries) {
                                                updateExercise(weekIndex, sessionIndex, exerciseIndex, 'is_triseries', false);
                                              }
                                            }}
                                            className={`px-2 py-1 text-xs rounded-sm transition-colors ${
                                              exercise.is_biseries 
                                                ? 'bg-purple-500 text-white' 
                                                : 'bg-zinc-800 text-zinc-400 hover:bg-purple-500/20 hover:text-purple-300'
                                            }`}
                                          >
                                            ⚡ Biserie
                                          </button>
                                          {/* Toggle Triserie */}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              // Al activar triserie, también activamos biserie (para 2º ejercicio)
                                              const newVal = !exercise.is_triseries;
                                              updateExercise(weekIndex, sessionIndex, exerciseIndex, 'is_triseries', newVal);
                                              if (newVal && !exercise.is_biseries) {
                                                updateExercise(weekIndex, sessionIndex, exerciseIndex, 'is_biseries', true);
                                              }
                                            }}
                                            className={`px-2 py-1 text-xs rounded-sm transition-colors ${
                                              exercise.is_triseries 
                                                ? 'bg-amber-500 text-zinc-900 font-semibold' 
                                                : 'bg-zinc-800 text-zinc-400 hover:bg-amber-500/20 hover:text-amber-300'
                                            }`}
                                          >
                                            ⚡⚡ Triserie
                                          </button>
                                          <Button 
                                            type="button" 
                                            variant="ghost" 
                                            size="icon"
                                            onClick={() => removeExercise(weekIndex, sessionIndex, exerciseIndex)}
                                            className="text-destructive h-8 w-8"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </div>

                                      {/* Selector de segundo ejercicio si es biserie */}
                                      {exercise.is_biseries && (
                                        <div className="mb-3 p-2 bg-purple-500/5 rounded-sm border border-purple-500/20">
                                          <Label className="text-xs text-purple-300 mb-1 block">Segundo ejercicio de la {exercise.is_triseries ? 'triserie' : 'biserie'}:</Label>
                                          <ExerciseSearchSelect
                                            exercises={exercises}
                                            value={exercise.biseries_exercise_id || ''}
                                            excludeIds={[exercise.exercise_id]}
                                            placeholder="Selecciona el 2º ejercicio..."
                                            accent="purple"
                                            testId={`biseries-search-${weekIndex}-${sessionIndex}-${exerciseIndex}`}
                                            onChange={(id, picked) => {
                                              updateExercise(weekIndex, sessionIndex, exerciseIndex, 'biseries_exercise_id', id);
                                              updateExercise(weekIndex, sessionIndex, exerciseIndex, 'biseries_exercise_name', picked.name);
                                            }}
                                          />
                                        </div>
                                      )}

                                      {/* Selector de tercer ejercicio si es triserie */}
                                      {exercise.is_triseries && (
                                        <div className="mb-3 p-2 bg-amber-500/5 rounded-sm border border-amber-500/20">
                                          <Label className="text-xs text-amber-300 mb-1 block">Tercer ejercicio de la triserie:</Label>
                                          <ExerciseSearchSelect
                                            exercises={exercises}
                                            value={exercise.triseries_exercise_id || ''}
                                            excludeIds={[exercise.exercise_id, exercise.biseries_exercise_id]}
                                            placeholder="Selecciona el 3er ejercicio..."
                                            accent="amber"
                                            testId={`triseries-search-${weekIndex}-${sessionIndex}-${exerciseIndex}`}
                                            onChange={(id, picked) => {
                                              updateExercise(weekIndex, sessionIndex, exerciseIndex, 'triseries_exercise_id', id);
                                              updateExercise(weekIndex, sessionIndex, exerciseIndex, 'triseries_exercise_name', picked.name);
                                            }}
                                          />
                                        </div>
                                      )}

                                      {/* Configuración del ejercicio */}
                                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                        <Input
                                          type="number"
                                          value={exercise.sets}
                                          onChange={(e) => updateExercise(weekIndex, sessionIndex, exerciseIndex, 'sets', parseInt(e.target.value))}
                                          className="bg-zinc-900/50 border-zinc-800 h-8 text-sm"
                                          placeholder="Series"
                                        />
                                        <Input
                                          value={exercise.reps}
                                          onChange={(e) => updateExercise(weekIndex, sessionIndex, exerciseIndex, 'reps', e.target.value)}
                                          className="bg-zinc-900/50 border-zinc-800 h-8 text-sm"
                                          placeholder="Reps"
                                        />
                                        <Input
                                          value={exercise.rest}
                                          onChange={(e) => updateExercise(weekIndex, sessionIndex, exerciseIndex, 'rest', e.target.value)}
                                          className="bg-zinc-900/50 border-zinc-800 h-8 text-sm"
                                          placeholder="Descanso"
                                        />
                                        <Select 
                                          value={exercise.tempo || '1:2'} 
                                          onValueChange={(value) => updateExercise(weekIndex, sessionIndex, exerciseIndex, 'tempo', value)}
                                        >
                                          <SelectTrigger className="bg-zinc-900/50 border-zinc-800 h-8 text-sm">
                                            <div className="flex items-center gap-1">
                                              <Clock className="h-3 w-3 text-primary" />
                                              <SelectValue placeholder="Tempo" />
                                            </div>
                                          </SelectTrigger>
                                          <SelectContent>
                                            {TEMPO_OPTIONS.map((tempo) => (
                                              <SelectItem key={tempo.value} value={tempo.value}>
                                                <span className="font-mono">{tempo.label}</span> - {tempo.description}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Input
                                          value={exercise.notes || ''}
                                          onChange={(e) => updateExercise(weekIndex, sessionIndex, exerciseIndex, 'notes', e.target.value)}
                                          className="bg-zinc-900/50 border-zinc-800 h-8 text-sm col-span-2 md:col-span-1"
                                          placeholder="Notas..."
                                        />
                                      </div>
                                    </div>
                                    {/* Botón insertar después de cada ejercicio */}
                                    <div className="flex justify-center">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setInsertPosition({ weekIndex, sessionIndex, position: exerciseIndex + 1 });
                                          setShowExerciseSearch(true);
                                        }}
                                        className="px-3 py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-sm transition-colors flex items-center gap-1"
                                        title="Insertar ejercicio aquí"
                                      >
                                        <Plus className="h-3 w-3" /> Insertar aquí
                                      </button>
                                    </div>
                                    </React.Fragment>
                                  ))}

                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full border-dashed border-zinc-700 text-muted-foreground hover:text-foreground"
                                    onClick={() => openExercisePicker(weekIndex, sessionIndex, 'strength')}
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Añadir ejercicio de fuerza
                                  </Button>
                                </TabsContent>

                                {/* TAB: RESISTENCIA/CARDIO */}
                                <TabsContent value="resistencia" className="space-y-3 mt-4">
                                  {session.cardio_blocks?.map((cardio, cardioIndex) => (
                                    <Card key={cardioIndex} className="bg-red-500/5 border-red-500/20">
                                      <CardContent className="p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            <span className="text-2xl">{CARDIO_MACHINES.find(m => m.value === cardio.machine)?.icon || '🏃'}</span>
                                            <Select 
                                              value={cardio.machine} 
                                              onValueChange={(value) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'machine', value)}
                                            >
                                              <SelectTrigger className="bg-zinc-900/50 border-zinc-800 w-48">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {CARDIO_MACHINES.map((machine) => (
                                                  <SelectItem key={machine.value} value={machine.value}>
                                                    {machine.icon} {machine.label}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <Button 
                                            type="button" 
                                            variant="ghost" 
                                            size="icon"
                                            onClick={() => removeCardioBlock(weekIndex, sessionIndex, cardioIndex)}
                                            className="text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>

                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Tiempo Total</Label>
                                            <Input
                                              value={cardio.total_time || ''}
                                              onChange={(e) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'total_time', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="20min"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Series</Label>
                                            <Input
                                              type="number"
                                              value={cardio.sets || ''}
                                              onChange={(e) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'sets', parseInt(e.target.value))}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="1"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Tiempo/Serie</Label>
                                            <Input
                                              value={cardio.set_time || ''}
                                              onChange={(e) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'set_time', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="5min"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Descanso Series</Label>
                                            <Input
                                              value={cardio.rest_between_sets || ''}
                                              onChange={(e) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'rest_between_sets', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="60s"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-muted-foreground">Intensidad</Label>
                                            <Select 
                                              value={cardio.intensity || 'media'} 
                                              onValueChange={(value) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'intensity', value)}
                                            >
                                              <SelectTrigger className="bg-zinc-900/50 border-zinc-800 h-9">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {INTENSITY_LEVELS.map((level) => (
                                                  <SelectItem key={level.value} value={level.value}>
                                                    <span className={level.color}>{level.label}</span>
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        </div>

                                        <Input
                                          value={cardio.notes || ''}
                                          onChange={(e) => updateCardioBlock(weekIndex, sessionIndex, cardioIndex, 'notes', e.target.value)}
                                          className="bg-zinc-900/50 border-zinc-800"
                                          placeholder="Notas (ej: mantener 120-140 ppm, inclinación 5%...)"
                                        />
                                      </CardContent>
                                    </Card>
                                  ))}

                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => addCardioBlock(weekIndex, sessionIndex)}
                                    className="w-full border-dashed border-red-500/30 text-red-400 hover:bg-red-500/10"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Añadir Bloque de Resistencia
                                  </Button>
                                </TabsContent>

                                {/* TAB: CIRCUITOS */}
                                <TabsContent value="circuitos" className="space-y-3 mt-4">
                                  {session.circuit_blocks?.map((circuit, circuitIndex) => (
                                    <Card key={circuitIndex} className="bg-green-500/5 border-green-500/20">
                                      <CardContent className="p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <RotateCcw className="h-5 w-5 text-green-400" />
                                            <Input
                                              value={circuit.name || ''}
                                              onChange={(e) => updateCircuitBlock(weekIndex, sessionIndex, circuitIndex, 'name', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 w-48"
                                              placeholder="Nombre del circuito"
                                            />
                                          </div>
                                          <Button 
                                            type="button" 
                                            variant="ghost" 
                                            size="icon"
                                            onClick={() => removeCircuitBlock(weekIndex, sessionIndex, circuitIndex)}
                                            className="text-destructive"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>

                                        {/* Circuit Config */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-green-500/10 rounded-sm">
                                          <div className="space-y-1">
                                            <Label className="text-xs text-green-400">Rondas</Label>
                                            <Input
                                              type="number"
                                              value={circuit.rounds || 3}
                                              onChange={(e) => updateCircuitBlock(weekIndex, sessionIndex, circuitIndex, 'rounds', parseInt(e.target.value))}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-green-400">Tiempo/Ejercicio</Label>
                                            <Input
                                              value={circuit.exercise_time || ''}
                                              onChange={(e) => updateCircuitBlock(weekIndex, sessionIndex, circuitIndex, 'exercise_time', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="40s"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-green-400">Descanso/Ejercicio</Label>
                                            <Input
                                              value={circuit.rest_between_exercises || ''}
                                              onChange={(e) => updateCircuitBlock(weekIndex, sessionIndex, circuitIndex, 'rest_between_exercises', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="20s"
                                            />
                                          </div>
                                          <div className="space-y-1">
                                            <Label className="text-xs text-green-400">Descanso/Ronda</Label>
                                            <Input
                                              value={circuit.rest_between_rounds || ''}
                                              onChange={(e) => updateCircuitBlock(weekIndex, sessionIndex, circuitIndex, 'rest_between_rounds', e.target.value)}
                                              className="bg-zinc-900/50 border-zinc-800 h-9"
                                              placeholder="90s"
                                            />
                                          </div>
                                        </div>

                                        {/* Circuit Exercises */}
                                        <div className="space-y-2">
                                          <Label className="text-sm text-muted-foreground">Ejercicios del circuito:</Label>
                                          {circuit.exercises?.map((ex, exIndex) => (
                                            <div key={exIndex} className="flex items-center gap-2 p-2 bg-black/20 rounded-sm">
                                              <span className="w-6 h-6 flex items-center justify-center bg-green-500/20 text-green-400 rounded-full text-xs font-bold">
                                                {exIndex + 1}
                                              </span>
                                              <span className="flex-1 text-sm">{ex.exercise_name}</span>
                                              <Input
                                                value={ex.time || ''}
                                                onChange={(e) => updateCircuitExercise(weekIndex, sessionIndex, circuitIndex, exIndex, 'time', e.target.value)}
                                                className="bg-zinc-900/50 border-zinc-800 h-7 w-20 text-sm"
                                                placeholder="40s"
                                              />
                                              <span className="text-xs text-muted-foreground">o</span>
                                              <Input
                                                value={ex.reps || ''}
                                                onChange={(e) => updateCircuitExercise(weekIndex, sessionIndex, circuitIndex, exIndex, 'reps', e.target.value)}
                                                className="bg-zinc-900/50 border-zinc-800 h-7 w-20 text-sm"
                                                placeholder="12 reps"
                                              />
                                              <Button 
                                                type="button" 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => removeCircuitExercise(weekIndex, sessionIndex, circuitIndex, exIndex)}
                                                className="text-destructive h-7 w-7"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ))}

                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="w-full border-dashed border-zinc-700 text-muted-foreground hover:text-foreground"
                                            onClick={() => openExercisePicker(weekIndex, sessionIndex, 'circuit', circuitIndex)}
                                          >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Añadir ejercicio al circuito
                                          </Button>
                                        </div>

                                        <Input
                                          value={circuit.notes || ''}
                                          onChange={(e) => updateCircuitBlock(weekIndex, sessionIndex, circuitIndex, 'notes', e.target.value)}
                                          className="bg-zinc-900/50 border-zinc-800"
                                          placeholder="Notas del circuito..."
                                        />
                                      </CardContent>
                                    </Card>
                                  ))}

                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => addCircuitBlock(weekIndex, sessionIndex)}
                                    className="w-full border-dashed border-green-500/30 text-green-400 hover:bg-green-500/10"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Añadir Circuito
                                  </Button>
                                </TabsContent>
                              </Tabs>
                            </CardContent>
                          </Card>
                        ))}

                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => addSession(weekIndex)}
                          className="w-full border-dashed"
                          data-testid={`add-session-${weekIndex}`}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {t('addSession')}
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </form>

      {/* Exercise Picker Dialog */}
      <Dialog open={exercisePickerOpen || showExerciseSearch} onOpenChange={(open) => {
        setExercisePickerOpen(open);
        if (!open) {
          setShowExerciseSearch(false);
          setInsertPosition(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Dumbbell className="h-5 w-5 text-primary" />
              {insertPosition ? `Insertar ejercicio en posición ${insertPosition.position + 1}` : 'Seleccionar Ejercicio'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={exerciseSearch}
                onChange={(e) => setExerciseSearch(e.target.value)}
                placeholder="Buscar ejercicio..."
                className="pl-10 bg-zinc-900/50 border-zinc-800"
              />
            </div>
            
            {/* Muscle Group Filters */}
            <div className="flex flex-wrap gap-2">
              {MUSCLE_GROUPS.map((group) => (
                <Button
                  key={group.key}
                  type="button"
                  variant={exerciseFilter === group.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setExerciseFilter(group.key)}
                  className={`text-xs ${exerciseFilter === group.key ? group.color : 'border-zinc-700'}`}
                >
                  {group.label}
                  {exerciseFilter === group.key && group.key !== 'all' && (
                    <X className="h-3 w-3 ml-1" onClick={(e) => { e.stopPropagation(); setExerciseFilter('all'); }} />
                  )}
                </Button>
              ))}
            </div>
            
            {/* Results count */}
            <p className="text-sm text-muted-foreground">
              {filteredExercises.length} ejercicio{filteredExercises.length !== 1 ? 's' : ''} encontrado{filteredExercises.length !== 1 ? 's' : ''}
            </p>
            
            {/* Exercise List */}
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {filteredExercises.map((exercise) => {
                  const group = getMuscleGroup(exercise);
                  return (
                    <div
                      key={exercise.id}
                      onClick={() => handleExerciseSelect(exercise.id)}
                      className="p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-800/50 hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{exercise.name}</p>
                          {exercise.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{exercise.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${group.color} text-white`}>
                            {group.label}
                          </span>
                          {exercise.video_url && (
                            <Video className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {filteredExercises.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Dumbbell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No se encontraron ejercicios</p>
                    <p className="text-xs mt-1">Prueba con otro filtro o búsqueda</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </LayoutComponent>
  );
};

export default PlanBuilderPage;
